import { FC, useState } from 'react'
import clsx from 'clsx'
import { Icon } from '@iconify/react'
import { useQueryClient } from '@tanstack/react-query'
import { Button, PopOver, useShowMessage } from '@pepper/ui'
import { disconnectGoogleAccount } from '@pepper/utils/src/api/integrations/google'
import { ContentRefreshDomain, GaGscType } from '@pepper/utils/src/types/domain'

import DisconnectConfirmation from 'components/DisconnectConfirmation'
import GoogleAccountPropertySelector from 'components/GoogleAccountPropertySelector'

import s from './GoogleAccountProperties.module.css'

type DropdownOption = {
  label: {
    header: string
    subHeader?: string
  }
  value: string
}

type Props = {
  userEmail: string
  googleAccountProperties: {
    gaOptions: DropdownOption[]
    gscOptions: DropdownOption[]
  }
  domains: ContentRefreshDomain[]
  accountId: string
  companyId: string
  onChange: (name: GaGscType, value?: string) => void
  googleIconUrl?: string
  isGaValid?: boolean
  isGscValid?: boolean
  gaMatch?: string
  gscMatch?: string
}

export const GoogleAccountProperties: FC<Props> = ({
  userEmail,
  googleAccountProperties: { gaOptions, gscOptions },
  domains,
  accountId,
  companyId,
  onChange,
  googleIconUrl,
  isGaValid,
  isGscValid,
  gaMatch,
  gscMatch,
}) => {
  const [showDisconnectConfirmation, setShowDisconnectConfirmation] =
    useState(false)
  const showMessage = useShowMessage()
  const queryClient = useQueryClient()

  const [isExpanded, setIsExpanded] = useState(true)

  const handleToggle = () => {
    setShowDisconnectConfirmation(prevVal => !prevVal)
  }

  const handleDisconnect = async () => {
    const response = await disconnectGoogleAccount({
      accountId,
      companyId,
    })

    if (response.status === 'error') {
      showMessage({ type: 'error', message: response.error })
      return
    }
    showMessage({
      type: 'success',
      message: 'Account disconnected successfully.',
    })

    queryClient.invalidateQueries(['profile-integrations', companyId])
    queryClient.invalidateQueries(['content-refresh-domains', companyId])

    setShowDisconnectConfirmation(false)
  }

  const getGaProperty = (value?: string) => {
    return value?.split('_')[0]
  }

  return (
    <>
      <div className={s.wrapper}>
        <div className={s.email}>
          <img className={s.icon} alt="google" src={googleIconUrl} />
          {userEmail}
          {gaMatch && gscMatch ? null : (
            <Icon className={s.alert} icon="lucide:alert-triangle" />
          )}
        </div>
        {gaMatch && gscMatch ? (
          <Button
            iconPlacement="right"
            type="text"
            icon={<Icon icon="lucide:chevron-down" width={16} />}
            onClick={() => setIsExpanded(prevVal => !prevVal)}
          >
            View Properties
          </Button>
        ) : (
          <PopOver
            visible={showDisconnectConfirmation}
            trigger="click"
            content={
              <div className={s.disconnectPopover}>
                <DisconnectConfirmation
                  onDisconnect={handleDisconnect}
                  connectedDomains={domains}
                  onCancel={handleToggle}
                />
              </div>
            }
            placement="leftTop"
          >
            <Button
              danger={true}
              type="link"
              size="middle"
              onClick={handleToggle}
            >
              Try Another Account
            </Button>
          </PopOver>
        )}
      </div>
      {isExpanded && (
        <div
          className={clsx(s.selectorWrapper, {
            [s.show]: isExpanded,
          })}
        >
          <GoogleAccountPropertySelector
            title="Google Search Console"
            name="gsc"
            onChange={(value?: string) =>
              onChange('gsc_domain_property', value)
            }
            options={gscOptions}
            isValid={isGscValid}
            value={gscMatch}
          />
          <GoogleAccountPropertySelector
            title="Google Analytics"
            name="ga"
            onChange={(value?: string) =>
              onChange('ga_property', getGaProperty(value))
            }
            options={gaOptions}
            isValid={isGaValid}
            value={gaMatch}
          />
        </div>
      )}
    </>
  )
}
